import React from 'react'

/**
 * Scanner device popup — same shell as Appointment / SelectPrescriptionProvider.
 * Close in header; Scan / Finish actions in footer. Wider top-right panel.
 */
export default function ScanFromDeviceModal(props) {
  const {
    open,
    scanning,
    scanMode,
    selectedDeviceId,
    scannerDevices,
    devicesLoaded,
    multiActive,
    pageCount,
    onClose,
    onChangeMode,
    onChangeDevice,
    onScanPage,
    onFinish,
    onCancel,
    onRefreshDevices,
  } = props

  if (!open) return null

  const selectedDevice = scannerDevices.find((d) => String(d.id) === String(selectedDeviceId))
  const canScan = !!selectedDeviceId && !scanning && devicesLoaded
  const modeLocked = scanning || multiActive

  const primaryLabel = scanning
    ? 'Scanning…'
    : scanMode === 'single'
      ? 'Scan'
      : multiActive
        ? 'Scan Next Page'
        : 'Scan Page'

  return (
    <div className="appointment-modal">
      <div
        className="modal book-appointment popperAppointment w-0 h-0 position-fixed"
        id="scanFromDevice-modal"
        data-backdrop="static"
      >
        <div
          className="modal-dialog"
          style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            left: 'auto',
            margin: 0,
            maxWidth: '40rem',
            width: '100%',
          }}
        >
          <div className="modal-content border-0 shadow">
            <div className="modal-header p-5 align-items-center">
              <h6 className="modal-title mb-0">
                {scanMode === 'multi' ? 'Scan Multi-Page Document' : 'Scan From Device'}
              </h6>
              <button
                type="button"
                className="btn btn-outline-primary"
                disabled={scanning && !multiActive}
                onClick={multiActive ? onCancel : onClose}
              >
                {multiActive ? 'Cancel' : 'Close'}
              </button>
            </div>

            <div className="modal-body px-5 pt-5 pb-0">
              <p className="text-muted small mb-5">
                Uses the paired iTRUST Connect app on this store&apos;s computer.
              </p>

              <div className="form-group mb-5">
                <label className="mb-3 d-block">Scan Type</label>
                <div className="row mx-n2">
                  <div className="col-6 px-2">
                    <div
                      className={`bg-light px-4 py-4 border rounded pointer h-100 ${scanMode === 'single' ? 'border-primary' : ''}`}
                      onClick={() => !modeLocked && onChangeMode('single')}
                      style={{ cursor: modeLocked ? 'not-allowed' : 'pointer', opacity: modeLocked && scanMode !== 'single' ? 0.6 : 1 }}
                    >
                      <div className={`font-weight-bold ${scanMode === 'single' ? 'text-primary' : ''}`}>1 Page</div>
                      <div className="small text-muted mb-0">One sheet → one PDF</div>
                    </div>
                  </div>
                  <div className="col-6 px-2">
                    <div
                      className={`bg-light px-4 py-4 border rounded pointer h-100 ${scanMode === 'multi' ? 'border-primary' : ''}`}
                      onClick={() => !modeLocked && onChangeMode('multi')}
                      style={{ cursor: modeLocked ? 'not-allowed' : 'pointer', opacity: modeLocked && scanMode !== 'multi' ? 0.6 : 1 }}
                    >
                      <div className={`font-weight-bold ${scanMode === 'multi' ? 'text-primary' : ''}`}>Multi Page</div>
                      <div className="small text-muted mb-0">Scan pages, then Finish</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group mb-5">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <label className="mb-0">Scanner Device</label>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    disabled={scanning || multiActive}
                    onClick={onRefreshDevices}
                  >
                    Refresh
                  </button>
                </div>
                <select
                  className="form-control"
                  value={selectedDeviceId}
                  disabled={modeLocked || !devicesLoaded}
                  onChange={(e) => onChangeDevice(e.target.value)}
                >
                  {!devicesLoaded && <option value="">Looking for online devices…</option>}
                  {devicesLoaded && !scannerDevices.length && (
                    <option value="">No online devices — open iTRUST Connect</option>
                  )}
                  {scannerDevices.map((device) => (
                    <option key={device.id} value={device.id}>
                      {device.name}{device.status ? ` (${device.status})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-light border rounded px-4 py-4 mb-2">
                {scanning ? (
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary mr-4" role="status" />
                    <div>
                      <div className="font-weight-bold">
                        {scanMode === 'multi' && multiActive && pageCount > 0
                          ? 'Scanning next page…'
                          : 'Scanning…'}
                      </div>
                      <div className="text-muted small mb-0">
                        Place the document on the scanner
                        {selectedDevice ? ` · ${selectedDevice.name}` : ''}
                      </div>
                    </div>
                  </div>
                ) : multiActive ? (
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <div className="font-weight-bold">
                        {pageCount} page{pageCount === 1 ? '' : 's'} ready
                      </div>
                      <div className="text-muted small mb-0">
                        Change the paper, scan again, or Finish PDF
                      </div>
                    </div>
                    <span className="badge badge-primary px-4 py-2">{pageCount}</span>
                  </div>
                ) : (
                  <div>
                    <div className="font-weight-bold">
                      {scanMode === 'single' ? 'Ready to scan one page' : 'Ready for multi-page scan'}
                    </div>
                    <div className="text-muted small mb-0">
                      {scanMode === 'single'
                        ? 'Click Scan — a PDF uploads when complete.'
                        : 'Scan each page, then click Finish PDF to upload.'}
                    </div>
                  </div>
                )}
              </div>

              {devicesLoaded && !scannerDevices.length && (
                <div className="alert alert-warning py-4 mt-5 mb-0">
                  No online scanner devices for this store. Pair and open <strong>iTRUST Connect</strong> on the clinic PC, then Refresh.
                </div>
              )}
            </div>

            <div className="modal-footer px-5 pb-7 pt-5 border-0 justify-content-end">
              {scanMode === 'multi' && (
                <button
                  type="button"
                  className="btn btn-success mr-4"
                  disabled={scanning || !multiActive || pageCount < 1}
                  onClick={onFinish}
                >
                  Finish PDF
                </button>
              )}
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canScan}
                onClick={onScanPage}
              >
                {primaryLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
